import type {
  DashboardCardItemKind,
  DashboardCardItemKindDefinition,
  DashboardChartSourceDefinition,
  DashboardChartSourceKey,
  DashboardMetricDefinition,
} from '../types';

export const DASHBOARD_CARD_ITEM_KIND_CATALOG: ReadonlyArray<DashboardCardItemKindDefinition> = [
  {
    kind: 'metric',
    label: 'Number',
    icon: 'pin',
    description: 'A single metric such as GRR, NPS, or a review-source rating.',
  },
  {
    kind: 'chart',
    label: 'Chart',
    icon: 'show_chart',
    description: 'Visualise a dataset as a line, bar, area, or donut chart.',
  },
  {
    kind: 'text',
    label: 'Text',
    icon: 'text_fields',
    description: 'Free-form heading and body text — useful for notes and section breaks.',
  },
  {
    kind: 'table',
    label: 'Table',
    icon: 'table_chart',
    description: 'A data table for displaying structured rows and columns.',
  },
];

export const DASHBOARD_CARD_ITEM_KIND_INDEX: Readonly<
  Record<DashboardCardItemKind, DashboardCardItemKindDefinition>
> = DASHBOARD_CARD_ITEM_KIND_CATALOG.reduce(
  (acc, def) => ({ ...acc, [def.kind]: def }),
  {} as Record<DashboardCardItemKind, DashboardCardItemKindDefinition>,
);

export const DASHBOARD_METRIC_CATALOG: ReadonlyArray<DashboardMetricDefinition> = [
  {
    key: 'grr',
    group: 'Headline KPIs',
    label: 'GuestRevu Rating (GRR)',
    icon: 'task_alt',
    logoSrc: '/assets/images/logos/icon.svg',
    value: '86%',
    tone: 'brand',
    description: 'Overall blended rating across all feedback channels.',
    badge: { value: '+4%', direction: 'up' },
    detail: '200 reviews',
  },
  {
    key: 'survey-review-rating',
    group: 'Headline KPIs',
    label: 'Survey Review Rating',
    icon: 'rate_review',
    value: '82%',
    tone: 'brand',
    description: 'Rating averaged across direct guest survey responses.',
    badge: { value: '+2%', direction: 'up' },
    detail: '118 surveys · 16% response',
  },
  {
    key: 'online-review-rating',
    group: 'Headline KPIs',
    label: 'Online Review Rating',
    icon: 'public',
    value: '89%',
    tone: 'success',
    description: 'Blended rating from connected online review sources.',
    badge: { value: '-1%', direction: 'down' },
    detail: '82 online reviews',
  },
  {
    key: 'compset-position',
    group: 'Headline KPIs',
    label: 'Compset Position',
    icon: 'leaderboard',
    value: '#4',
    tone: 'neutral',
    description: 'Your ranking against the configured competitor set.',
    badge: { value: '-1', direction: 'down' },
    detail: 'of 6 competitors',
  },
  {
    key: 'tripadvisor-rating',
    group: 'Review Sources',
    label: 'Tripadvisor Rating',
    icon: 'travel_explore',
    logoSrc: '/assets/images/logos/tripadvisor.svg',
    value: '4.0 / 5',
    tone: 'brand',
    description: 'Tripadvisor traveller rating.',
    detail: '1,368 reviews',
  },
  {
    key: 'booking-rating',
    group: 'Review Sources',
    label: 'Booking.com Rating',
    icon: 'hotel',
    logoSrc: '/assets/images/logos/booking.svg',
    value: '92%',
    tone: 'brand',
    description: 'Booking.com guest review score.',
    detail: '292 reviews',
  },
  {
    key: 'google-rating',
    group: 'Review Sources',
    label: 'Google Rating',
    icon: 'public',
    logoSrc: '/assets/images/logos/google.svg',
    value: '100%',
    tone: 'success',
    description: 'Average Google review rating.',
    detail: '1 review',
  },
  {
    key: 'expedia-rating',
    group: 'Review Sources',
    label: 'Expedia Rating',
    icon: 'flight_takeoff',
    logoSrc: '/assets/images/logos/expedia.svg',
    value: '—',
    tone: 'neutral',
    description: 'Connect Expedia to populate this metric.',
    detail: 'Not connected',
  },
  {
    key: 'nps-score',
    group: 'Sentiment & NPS',
    label: 'Net Promoter Score',
    icon: 'insights',
    value: '48',
    tone: 'success',
    description: 'Promoter percentage minus detractor percentage.',
    badge: { value: '+3', direction: 'up' },
    detail: 'Target: 50',
  },
  {
    key: 'sentiment-score',
    group: 'Sentiment & NPS',
    label: 'Sentiment Score',
    icon: 'mood',
    value: '74',
    tone: 'success',
    description: 'Composite sentiment score across review text.',
    badge: { value: '+1', direction: 'up' },
    detail: 'Positive 64% · Negative 12%',
  },
  {
    key: 'total-reviews',
    group: 'Volume',
    label: 'Total Reviews',
    icon: 'forum',
    value: '318',
    tone: 'brand',
    description: 'All reviews received in the current period.',
    badge: { value: '+18%', direction: 'up' },
  },
  {
    key: 'response-rate',
    group: 'Volume',
    label: 'Response Rate',
    icon: 'reply_all',
    value: '63%',
    tone: 'warning',
    description: 'Percentage of reviews replied to by the team.',
    badge: { value: '-2%', direction: 'down' },
  },
  {
    key: 'open-tickets',
    group: 'Service Tickets',
    label: 'Open Tickets',
    icon: 'confirmation_number',
    value: '14',
    tone: 'warning',
    description: 'Service tickets currently awaiting action.',
    badge: { value: '+3', direction: 'up' },
  },
  {
    key: 'resolved-tickets',
    group: 'Service Tickets',
    label: 'Resolved Tickets',
    icon: 'task',
    value: '102',
    tone: 'success',
    description: 'Service tickets resolved in the current period.',
    badge: { value: '+12', direction: 'up' },
  },
];

export const DASHBOARD_METRIC_INDEX: Readonly<Record<string, DashboardMetricDefinition>> =
  DASHBOARD_METRIC_CATALOG.reduce(
    (acc, def) => ({ ...acc, [def.key]: def }),
    {} as Record<string, DashboardMetricDefinition>,
  );

export const DASHBOARD_CHART_SOURCE_CATALOG: ReadonlyArray<DashboardChartSourceDefinition> = [
  {
    key: 'trend-analysis',
    label: 'Trend Analysis',
    description: 'Volume and rating across feedback and online sources over time.',
    defaultHeading: 'Trend Analysis',
    supportedTypes: ['line', 'area', 'bar', 'stacked-bar'],
    defaultType: 'line',
    availableOptions: [
      'granularity',
      'legendPosition',
      'showDataLabels',
      'yAxisMin',
      'yAxisMax',
      'colorScheme',
      'compareToPrevious',
    ],
    defaultDisplay: { legendPosition: 'top' },
  },
  {
    key: 'net-promoter-score',
    label: 'Net Promoter Score',
    description: 'NPS score over time and promoter mix.',
    defaultHeading: 'Net Promoter Score',
    supportedTypes: ['line', 'area', 'donut', 'stacked-bar'],
    defaultType: 'line',
    availableOptions: [
      'granularity',
      'legendPosition',
      'showDataLabels',
      'yAxisMin',
      'yAxisMax',
      'colorScheme',
      'compareToPrevious',
    ],
    defaultDisplay: {
      legendPosition: 'top',
    },
  },
  {
    key: 'sentiment',
    label: 'Sentiment',
    description: 'Positive, neutral and negative review distribution.',
    defaultHeading: 'Sentiment',
    supportedTypes: ['donut', 'bar', 'bar-horizontal', 'line', 'area'],
    defaultType: 'donut',
    availableOptions: [
      'legendPosition',
      'showDataLabels',
      'yAxisMin',
      'yAxisMax',
      'colorScheme',
      'compareToPrevious',
    ],
    defaultDisplay: { legendPosition: 'top' },
  },
  {
    key: 'service-ratings',
    label: 'Primary Service Ratings',
    description: 'Service-level scores against high, average, low and target thresholds.',
    defaultHeading: 'Primary Service Ratings',
    supportedTypes: ['bar', 'bar-horizontal', 'line', 'rating-bars'],
    defaultType: 'bar',
    availableOptions: [
      'legendPosition',
      'showDataLabels',
      'yAxisMin',
      'yAxisMax',
      'topN',
      'sortOrder',
      'colorScheme',
    ],
    defaultDisplay: {
      legendPosition: 'top',
      sortOrder: 'value-desc',
    },
  },
  {
    key: 'competitor',
    label: 'Competitor',
    description: 'Comparison of your property against the competitive set.',
    defaultHeading: 'Competitor Performance',
    supportedTypes: ['bar', 'bar-horizontal', 'stacked-bar', 'line', 'area'],
    defaultType: 'bar',
    availableOptions: [
      'legendPosition',
      'showDataLabels',
      'yAxisMin',
      'yAxisMax',
      'sortOrder',
      'colorScheme',
    ],
    defaultDisplay: { legendPosition: 'top', sortOrder: 'value-desc' },
  },
  {
    key: 'review-source-volume',
    label: 'Review Source Volume',
    description: 'Number of reviews broken down by review source.',
    defaultHeading: 'Review Source Volume',
    supportedTypes: ['source-list', 'bar', 'bar-horizontal', 'donut'],
    defaultType: 'source-list',
    availableOptions: [
      'legendPosition',
      'showDataLabels',
      'yAxisMin',
      'yAxisMax',
      'topN',
      'sortOrder',
      'colorScheme',
    ],
    defaultDisplay: { legendPosition: 'top', sortOrder: 'value-desc' },
  },
  {
    key: 'custom',
    label: 'Custom Chart',
    description: 'Build a chart from your own data dimensions and measures.',
    defaultHeading: 'Custom Chart',
    supportedTypes: ['line', 'area', 'bar', 'bar-horizontal', 'stacked-bar', 'rating-bars'],
    defaultType: 'bar',
    availableOptions: ['legendPosition', 'showDataLabels', 'colorScheme', 'granularity'],
    defaultDisplay: { legendPosition: 'top' },
  },
];

export const DASHBOARD_CHART_SOURCE_INDEX: Readonly<
  Record<DashboardChartSourceKey, DashboardChartSourceDefinition>
> = DASHBOARD_CHART_SOURCE_CATALOG.reduce(
  (acc, def) => ({ ...acc, [def.key]: def }),
  {} as Record<DashboardChartSourceKey, DashboardChartSourceDefinition>,
);

export interface DashboardReviewSourceDefinition {
  readonly key: string;
  readonly label: string;
  readonly logoSrc: string;
  readonly icon: string;
}

export const DASHBOARD_REVIEW_SOURCE_CATALOG: ReadonlyArray<DashboardReviewSourceDefinition> = [
  { key: 'guestrevu',   label: 'GuestRevu',  logoSrc: '/assets/images/logos/icon.svg',        icon: 'task_alt' },
  { key: 'google',      label: 'Google',      logoSrc: '/assets/images/logos/google.svg',      icon: 'public' },
  { key: 'tripadvisor', label: 'Tripadvisor', logoSrc: '/assets/images/logos/tripadvisor.svg', icon: 'travel_explore' },
  { key: 'expedia',     label: 'Expedia',     logoSrc: '/assets/images/logos/expedia.svg',     icon: 'flight_takeoff' },
  { key: 'booking_com', label: 'Booking.com', logoSrc: '/assets/images/logos/booking.svg',     icon: 'hotel' },
];

export const DASHBOARD_REVIEW_SOURCE_INDEX: Readonly<Record<string, DashboardReviewSourceDefinition>> =
  DASHBOARD_REVIEW_SOURCE_CATALOG.reduce(
    (acc, s) => ({ ...acc, [s.key]: s }),
    {} as Record<string, DashboardReviewSourceDefinition>,
  );
